import React from 'react'
import { Spin, Popconfirm } from 'antd'
import { DeleteOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'

import { 
    StyledModal, 
    AttributeGrid, 
    Attribute, 
    AttributeLabel, 
    AttributeValue, 
    SectionHeader, 
    SectionLabel, 
    CountBadge, 
    CloseButton, 
    EditButton, 
    DangerButton, 
    StyledTable 
} from './styles'

export const CompoundDetailModal = ({
    compound,
    dispenseUnits,
    dispenseUnitsLoading,
    onClose,
    onEdit,
    onDelete,
}) => {
    if (!compound) return null

    const resolveDispenseUnit = (id) => {
        const unit = dispenseUnits.find(
            u => u.StandardDispenseUnitTypeID?.toString() === id?.toString()
        )
        return unit?.Name ?? (id != null ? `ID: ${id}` : '—')
    }

    const dispenseUnitLabel = resolveDispenseUnit(compound.dispenseUnitId)

    const ingredientColumns = [
        {
            title: 'Unit of Measure',
            dataIndex: 'dispenseUnitId',
            key: 'dispenseUnitId',
            render: id => resolveDispenseUnit(id),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: val => val ?? '—',
        },
        {
            title: 'Strength',
            dataIndex: 'strength',
            key: 'strength',
            render: val => val ?? '—',
        },
        {
            title: 'Name',
            dataIndex: 'freeText',
            key: 'freeText',
            render: val => val ?? '—',
        },
    ]

    const ingredients = compound.ingredients ?? []

    return (
        <StyledModal
            title={compound.name}
            open={!!compound}
            onCancel={onClose}
            destroyOnClose
            centered
            width={680}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Popconfirm
                        title="Are you sure you want to delete this compound?"
                        onConfirm={() => onDelete(compound._id)}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                    >
                        <DangerButton icon={<DeleteOutlined />}>
                            Delete
                        </DangerButton>
                    </Popconfirm>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <EditButton icon={<EditOutlined />} onClick={() => onEdit(compound)}>
                            Edit
                        </EditButton>
                        <CloseButton icon={<CloseOutlined />} onClick={onClose}>
                            Close
                        </CloseButton>
                    </div>
                </div>
            }
        >
            <Spin spinning={dispenseUnitsLoading}>
                <AttributeGrid>
                    <Attribute>
                        <AttributeLabel>Dispense Type</AttributeLabel>
                        <AttributeValue>{dispenseUnitLabel}</AttributeValue>
                    </Attribute>
                    <Attribute>
                        <AttributeLabel>Schedule</AttributeLabel>
                        <AttributeValue>{compound.schedule ?? 'none'}</AttributeValue>
                    </Attribute>
                </AttributeGrid>

                {(compound.firstPrescriptionDiagnosis?.primaryDiagnosisId != null ||
                  compound.secondPrescriptionDiagnosis?.primaryDiagnosisId != null) && (
                    <>
                        <SectionHeader>
                            <SectionLabel>Diagnoses</SectionLabel>
                        </SectionHeader>
                        <AttributeGrid style={{ gridTemplateColumns: '1fr', marginBottom: 24 }}>
                            {compound.firstPrescriptionDiagnosis?.primaryDiagnosisId != null && (
                                <Attribute>
                                    <AttributeLabel>Primary Diagnosis</AttributeLabel>
                                    <AttributeValue>
                                        {compound.firstPrescriptionDiagnosis.primaryDiagnosisName
                                            ?? compound.firstPrescriptionDiagnosis.primaryDiagnosisId}
                                    </AttributeValue>
                                </Attribute>
                            )}
                            {compound.secondPrescriptionDiagnosis?.primaryDiagnosisId != null && (
                                <Attribute>
                                    <AttributeLabel>Secondary Diagnosis</AttributeLabel>
                                    <AttributeValue>
                                        {compound.secondPrescriptionDiagnosis.primaryDiagnosisName
                                            ?? compound.secondPrescriptionDiagnosis.primaryDiagnosisId}
                                    </AttributeValue>
                                </Attribute>
                            )}
                        </AttributeGrid>
                    </>
                )}

                <SectionHeader>
                    <SectionLabel>Ingredients</SectionLabel>
                    <CountBadge>{ingredients.length}</CountBadge>
                </SectionHeader>
                <StyledTable
                    dataSource={ingredients}
                    columns={ingredientColumns}
                    rowKey={(_, index) => index}
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No ingredients' }}
                />
            </Spin>
        </StyledModal>
    )
}
